import { ContactDetailsEnum } from "@/Orchestrator/Types/Enums/globalDefaultEnum";
import { countries } from "./countries";

// Longest-match first so ambiguous prefixes like "+1" vs "+1242" resolve to
// the most specific country code. Precomputed at module load — the country
// list is static.
const SORTED_CODES = countries
    .map((c) => c.countryCallingCode.replace(/\+/g, ""))
    .sort((a, b) => b.length - a.length);

// LRI…PDI (U+2066/U+2069) isolate the number as left-to-right so RTL layouts
// don't shuffle it into "797604620 (213+)" — the digits and "+" keep their
// logical order regardless of the surrounding paragraph direction.
const ltrIsolate = (s: string): string => `⁦${s}⁩`;

// The contact centre number is displayed as a vanity label — "800 RAIL (800
// 7245)" — where the parenthesised digits are the dialable form. Stripping
// non-digits off the whole label yields 8008007245, since "800" appears twice.
const toTelHref = (display: string): string => {
    const dialable = display.match(/\(([^)]*\d[^)]*)\)/)?.[1] ?? display;
    const digits = dialable.replace(/\D/g, "");
    // Only an explicit "+" marks an international number. UAE toll-free 800
    // numbers are dialled nationally and must not gain one.
    return digits ? `tel:${display.startsWith("+") ? "+" : ""}${digits}` : "";
};

// The CMS serves display and dial as one field, so the link is derived from
// whatever string we render — falling back to the built-in number if the CMS
// value carries no digits.
export const contactTelHref = (cmsPhone?: string | null): string =>
    toTelHref(cmsPhone?.trim() ?? "") || toTelHref(ContactDetailsEnum.PHONE);

// wa.me only resolves a full international number with no "+", but the CMS
// field is free text and the contact centre quotes the WhatsApp line in
// national form ("02-2038 401"). The trunk "0" is national-dialling notation —
// as a bare prefix, after an international prefix, or parenthesised inside a
// number already carrying the country code ("+971 (0)2 …"). Each must resolve
// to the same subscriber, or the link opens a chat with a number that has no
// WhatsApp account behind it.
const UAE_COUNTRY_CODE = "971";

const toWaDigits = (display: string): string =>
    display
        .replace(/\D/g, "")
        .replace(/^00/, "") // 00971 2 … → 971 2 …
        .replace(/^0/, UAE_COUNTRY_CODE) // 02 …     → 971 2 …
        .replace(/^9710/, UAE_COUNTRY_CODE); // +971 (0)2 … → 971 2 …

// Shorter than any international number, and long enough to reject a value
// that only became digits because the trunk-zero rule fired on junk ("0" alone
// would otherwise dial the country code).
const MIN_DIALABLE_DIGITS = 8;

// Same contract as contactTelHref: the CMS serves display and dial as one
// field, so fall back to the built-in number whenever the CMS value carries no
// dialable digits — not just when it is blank. No vanity form is expected on
// this field, so unlike toTelHref there is no parenthesised-label extraction.
export const whatsappHref = (cmsWhatsapp?: string | null): string => {
    const dialable = (display: string): string => {
        const digits = toWaDigits(display);
        return digits.length >= MIN_DIALABLE_DIGITS ? digits : "";
    };
    const digits =
        dialable(cmsWhatsapp?.trim() ?? "") ||
        dialable(ContactDetailsEnum.WHATSAPP);
    return digits ? `https://wa.me/${digits}` : "";
};

export const formatPhoneWithCode = (
    phone: string | undefined | null,
): string => {
    if (!phone) return "";
    const raw = phone.replace(/\+/g, "");
    const match = SORTED_CODES.find((code) => raw.startsWith(code));
    if (match) return ltrIsolate(`+${match} ${raw.slice(match.length)}`);
    return ltrIsolate(`+${raw}`);
};
